import { FC, ReactNode, useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { useDialog } from 'hooks/useDialog';
import { Button } from 'components/ui/Button';
import { GenericLoading } from 'components/ui/Loader';

interface ModalShellProps {
  title: string;
  subtitle?: string;
  isLoading?: boolean;
  /** Absent sur une modale de consultation : seul le bouton de fermeture est rendu. */
  submit?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    /** Action destructrice (suppression) : bouton rouge plutôt que couleur primaire. */
    destructive?: boolean;
  };
  closeLabel?: string;
  children: ReactNode;
}

/**
 * Coquille commune aux modales du back-office.
 *
 * Trois raisons de ne pas utiliser le rendu de dialogue par défaut : le bandeau de titre
 * coloré de l'ancien back-office, une largeur adaptée à des formulaires très denses, et
 * surtout un défilement interne au corps — l'en-tête et les boutons restent visibles,
 * alors que le dialogue standard fait défiler toute la page et éloigne le bouton
 * « Sauvegarder » à plusieurs écrans du champ en cours de saisie.
 */
export const ModalShell: FC<ModalShellProps> = ({
  title,
  subtitle,
  isLoading,
  submit,
  closeLabel = 'Fermer',
  children,
}) => {
  const { closeDialog } = useDialog();

  return (
    <div className="flex max-h-[88vh] flex-col">
      <header className="flex items-start justify-between gap-4 bg-primary px-6 py-4">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold text-white">{title}</h2>
          {subtitle && <p className="mt-0.5 text-sm text-white/70">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={closeDialog}
          aria-label="Fermer"
          className="flex-shrink-0 rounded-lg p-1 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      {/* Seul le corps défile : le bandeau et les actions restent à l'écran. */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/60 px-6 py-5">
        {isLoading ? <GenericLoading /> : children}
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-gray-200 bg-white px-6 py-3">
        <Button plain onClick={closeDialog}>
          {closeLabel}
        </Button>
        {submit && (
          <Button
            color={submit.destructive ? 'red' : 'primary'}
            onClick={submit.onClick}
            disabled={submit.disabled}
          >
            {submit.label}
          </Button>
        )}
      </footer>
    </div>
  );
};

/**
 * Section d'une modale : bandeau de titre discrètement coloré puis contenu sur fond
 * blanc. Découper les formulaires denses en blocs nommés évite le mur de champs de
 * l'ancien écran, où l'on ne savait plus à quelle entité se rapportait une saisie.
 */
export const ModalSection: FC<{
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  /** Action alignée à droite du titre, ex. « Ajouter un co-assureur ». */
  action?: ReactNode;
  /** Section repliable : l'en-tête bascule le contenu. Pour les blocs facultatifs et discrets. */
  collapsible?: boolean;
  /** État initial d'une section repliable (fermée par défaut). */
  defaultOpen?: boolean;
  children: ReactNode;
}> = ({ title, subtitle, icon, action, collapsible, defaultOpen = false, children }) => {
  const [isOpen, setIsOpen] = useState(!collapsible || defaultOpen);
  const header = (
    <>
      <div className="flex items-center gap-2">
        {collapsible && (
          <ChevronRight
            className={`h-4 w-4 text-primary transition-transform ${isOpen ? 'rotate-90' : ''}`}
          />
        )}
        {icon && <span className="text-primary">{icon}</span>}
        <h3 className="text-sm font-semibold text-primary">{title}</h3>
        {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
      </div>
      {action}
    </>
  );

  return (
    <section className="mb-5 overflow-hidden rounded-xl border border-gray-200 bg-white last:mb-0">
      {collapsible ? (
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((current) => !current)}
          className="flex w-full items-center justify-between gap-3 border-b border-gray-100 bg-primary/5 px-4 py-2.5 text-left"
        >
          {header}
        </button>
      ) : (
        <header className="flex items-center justify-between gap-3 border-b border-gray-100 bg-primary/5 px-4 py-2.5">
          {header}
        </header>
      )}
      {isOpen && <div className="p-4">{children}</div>}
    </section>
  );
};
